/**
 * Formats a date string to "DD MMM YYYY" format (e.g., "04 Mar 2026")
 * Used consistently across the entire application for date display
 * 
 * @param dateStr - Date string in format "YYYY-MM-DD" or any valid date format
 * @returns Formatted date string in "DD MMM YYYY" format
 */
export const formatDate = (dateStr: string | number): string => {
  try {
    // Handle both string dates and epoch milliseconds
    const date = typeof dateStr === 'number' 
      ? new Date(dateStr)
      : new Date(dateStr + "T00:00:00");
    
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
};

/**
 * Formats a date string to "DD MMM YYYY" format with time
 * @param dateStr - Date string or timestamp
 * @returns Formatted date string with time
 */
export const formatDateWithTime = (dateStr: string | number): string => {
  try {
    const date = typeof dateStr === 'number'
      ? new Date(dateStr)
      : new Date(dateStr);
    
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    console.error('Error formatting date with time:', error);
    return 'N/A';
  }
};

/**
 * Returns today's date in IST (Asia/Kolkata) as a YYYY-MM-DD string.
 * This ensures consistent date calculations regardless of the user's local timezone.
 */
export const todayIST = (): string => {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

/**
 * Returns the minimum allowed start date (today in IST + bufferDays) as YYYY-MM-DD.
 * @param bufferDays - Number of days to add to today's IST date
 */
export const getMinStartDate = (bufferDays: number): string => {
  // Get current date parts in IST
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());

  const year = parseInt(parts.find(p => p.type === 'year')!.value);
  const month = parseInt(parts.find(p => p.type === 'month')!.value) - 1;
  const day = parseInt(parts.find(p => p.type === 'day')!.value);

  const date = new Date(year, month, day + bufferDays);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};
